import DataBlock from './data_block';

export default class FontDefinition {
  constructor() {
    this.name = '';

    this.fontFamily = 'Arial';
    this.fontStyle = ''; // not supported yet (Condensed, Bold, Italic etc.)
    this.fontSize = 14;
    this.lineHeight = 'auto'; // line_height = (leading == 0) ? (font_size * 1.2) : (font_size + leading / 2)
    this.leading = 0;
    this.kerning = 0; // not supported, use tracking instead
    this.tracking = 0; // 1000 = 1em = font_size / 1000
    this.antialiasing = 'smooth'; // possible values: none, sharp, crisp, strong, smooth. not supported
    this.color = {
      r: 0,
      g: 0,
      b: 0,
      a: 255
    };
    this.verticalScale = 100;
    this.horizontalScale = 100;
    this.baselineShift = 0; // y offset

    this.fauxBold = false; // bold
    this.fauxItalic = false; // italic
    this.allCaps = false; // text-transform: uppercase
    this.smallCaps = false; // font-variant: small-caps
    this.superscript = false; // not supported yet
    this.subscript = false; // not supported yet
    this.underline = false; // text-decoration: underline
    this.strikethrough = false; // text-decoration: line-through
    this.language = 'Russian';

    this.textAlign = 'center';
    this.verticalAlign = 'center'; // не поддерживается фотошопом, эмулируется на уровне координат

    this.outlineSize = 'auto';
    this.outlineColor = {
      r: 255,
      g: 255,
      b: 255,
      a: 255
    };
  }

  export() {
    let data = {};
    Object.keys(this).forEach(key => {
      data[key] = this[key];
    });
    return data;
  }

  import(data) {
    Object.keys(data).forEach(key => {
      if (key in this) {
        this[key] = data[key];
      }
    });
  }

  clone() {
    let fd = new FontDefinition();
    fd.import(this.export());
    return fd;
  }

  getRealLineHeight() {
    let lineHeight = this.lineHeight;
    if (lineHeight == 'auto') {
      lineHeight = (this.leading == 0) ? (this.fontSize * 1.2) : (this.fontSize + this.leading / 2);
    }
    return parseFloat(lineHeight);
  }

  getRealOutlineSize() {
    let outlineSize = this.outlineSize;
    if (outlineSize == 'auto') {
      outlineSize = Math.max(0, Math.round(this.fontSize / 4, 2));
    }
    return parseFloat(outlineSize);
  }

  css(asHash) {
    let textDecoration = [];
    if (this.underline) {
      textDecoration.push('underline');
    }
    if (this.strikethrough) {
      textDecoration.push('line-through');
    }
    if (textDecoration.length == 0) {
      textDecoration = 'none';
    } else {
      textDecoration = textDecoration.join(' ');
    }
    let verticalAlign = this.verticalAlign;
    if (verticalAlign == 'center') {
      verticalAlign = 'middle';
    }
    let alignItems = {
      left: 'flex-start',
      center: 'center',
      right: 'flex-end'
    }[this.verticalAlign];
    let justifyContent = {
      left: 'flex-start',
      center: 'center',
      right: 'flex-end'
    }[this.textAlign];

    if (asHash) {
      return {
        fontFamily: this.fontFamily,
        fontSize: this.fontSize + 'px',
        lineHeight: this.getRealLineHeight() + 'px',
        color: `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${this.color.a / 255.0})`,
        fontWeight: this.fauxBold ? 'bold' : 'normal',
        fontStyle: this.fauxItalic ? 'italic' : 'normal',
        fontVariant: this.smallCaps ? 'small-caps' : 'normal',
        textDecoration: textDecoration,
        textAlign: this.textAlign,
        textTransform: this.allCaps ? 'uppercase' : 'none',
        webkitTextStroke: `${this.getRealOutlineSize()}px rgba(${this.outlineColor.r}, ${this.outlineColor.g}, ${this.outlineColor.b}, ${this.outlineColor.a / 255.0})`,
        webkitTextFillColor: `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${this.color.a / 255.0})`,
        verticalAlign: verticalAlign,
        alignItems: alignItems,
        justifyContent: justifyContent
      };
    } else {
      return `
        font-family: "${this.fontFamily}";
        font-size: ${this.fontSize}px;
        line-height: ${this.getRealLineHeight()}px;
        color: rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${this.color.a / 255.0});
        font-weight: ${this.fauxBold ? 'bold' : 'normal'};
        font-style: ${this.fauxItalic ? 'italic' : 'normal'};
        font-variant: ${this.smallCaps ? 'small-caps' : 'normal'};
        text-decoration: ${textDecoration};
        text-align: ${this.textAlign};
        text-transform: ${this.allCaps ? 'uppercase' : 'none'};
        -webkit-text-stroke: ${this.getRealOutlineSize()}px rgba(${this.outlineColor.r}, ${this.outlineColor.g}, ${this.outlineColor.b}, ${this.outlineColor.a / 255.0});
        -webkit-text-fill-color: rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${this.color.a / 255.0});
        vertical-align: ${verticalAlign};
        align-items: ${alignItems};
        justify-content: ${justifyContent};
      `;
    }
  }

  cssFont() {
    let props = [];
    if (this.fauxItalic)
      props.push('italic');
    if (this.smallCaps)
      props.push('small-caps');
    if (this.fauxBold)
      props.push('bold');
    props.push(this.fontSize + 'px/' + this.getRealLineHeight() + 'px');
    props.push(`"${this.fontFamily}"`);

    return props.join(' ');
  }

  detectFontStyles() {
    let alphabet = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let styles = [
      'italic',

      'bold',
      'bold italic',

      '100',
      '100 italic',
      '200',
      '200 italic',
      '300',
      '300 italic',
//      '400',
      '400 italic',
      '500',
      '500 italic',
      '600',
      '600 italic',
      '700',
      '700 italic',
      '800',
      '800 italic',
      '900',
      '900 italic'
    ];

    let availableStyles = [];
    let canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    let context = canvas.getContext('2d');

    context.font = `normal 144px ${this.fontFamily}`;
    let widths = {};
    widths[context.measureText(alphabet).width] = 'normal';

    styles.forEach(style => {
      context.font = `${style} 144px ${this.fontFamily}`;
      let width = context.measureText(alphabet).width;

      if (width in widths)
        return;

      widths[width] = style;
    });

    return Object.values(widths);
  }

  exportBinary() {
    let data = new DataBlock();
    data.putString(this.name);

    data.putString(this.fontFamily);
    data.putString(this.fontStyle);
    data.putDouble(this.fontSize);
    data.put(this.lineHeight == 'auto' ? 1 : 0);
    data.putDouble(this.getRealLineHeight());
    data.putDouble(this.leading);
    data.putDouble(this.kerning);
    data.putDouble(this.tracking);
    data.put(this.color.r, this.color.g, this.color.b, this.color.a);
    data.putDouble(this.verticalScale);
    data.putDouble(this.horizontalScale);
    data.putDouble(this.baselineShift);

    data.put(this.fauxBold ? 1 : 0);
    data.put(this.fauxItalic ? 1 : 0);
    data.put(this.allCaps ? 1 : 0);
    data.put(this.smallCaps ? 1 : 0);
    data.put(this.superscript ? 1 : 0);
    data.put(this.subscript ? 1 : 0);
    data.put(this.underline ? 1 : 0);
    data.put(this.strikethrough ? 1 : 0);
    data.putString(this.language);

    data.putString(this.textAlign);
    data.putString(this.verticalAlign);

    data.put(this.outlineSize == 'auto' ? 1 : 0);
    data.putDouble(this.getRealOutlineSize());
    data.put(this.outlineColor.r, this.outlineColor.g, this.outlineColor.b, this.outlineColor.a);

    return data;
  }

  importKaitai(data) {
    this.name = data.name.value;
    this.fontFamily = data.fontFamily.value;
    this.fontStyle = data.fontStyle.value;
    this.fontSize = data.fontSize;
    this.lineHeight = data.lineHeightAuto ? 'auto' : data.lineHeight;
    this.leading = data.leading;
    this.kerning = data.kerning;
    this.tracking = data.tracking;
    this.color = {
      r: data.color.r,
      g: data.color.g,
      b: data.color.b,
      a: data.color.a
    };
    this.verticalScale = data.verticalScale;
    this.horizontalScale = data.horizontalScale;
    this.baselineShift = data.baselineShift;

    this.fauxBold = data.fauxBold != 0;
    this.fauxItalic = data.fauxItalic != 0;
    this.allCaps = data.allCaps != 0;
    this.smallCaps = data.smallCaps != 0;
    this.superscript = data.superscript != 0;
    this.subscript = data.subscript != 0;
    this.underline = data.underline != 0;
    this.strikethrough = data.strikethrough != 0;
    this.language = data.language.value;

    this.textAlign = data.textAlign.value;
    this.verticalAlign = data.verticalAlign.value;

    this.outlineSize = data.outlineSizeAuto ? 'auto' : data.outlineSize;
    this.outlineColor = {
      r: data.outlineColor.r,
      g: data.outlineColor.g,
      b: data.outlineColor.b,
      a: data.outlineColor.a
    };
  }
}
